/**
 * CopilotPanel — Agentic AI Copilot panel
 * ui/src/components/shared/CopilotPanel.jsx
 * S239 — Phase 2 agentic session wiring
 *
 * Phase 2 additions over Phase 1:
 *   - On panel open: POST /api/v2/agent/sessions to create agent session,
 *     capture initial context_snapshot (updated_at timestamps for stale detection)
 *   - agentSessionId + snapshot stored in panel state
 *   - WorkflowTab and StatusTab receive snapshot for stale comparison
 *   - On stale detected: PATCH /api/v2/agent/sessions/{id}/snapshot to re-snapshot
 *   - Stale banner shown when any timestamp has drifted since snapshot
 *
 * Props:
 *   open           {bool}    — controlled by shell toggle
 *   stageName      {string}  — active nav label, passed from shell
 *   aiQueryEnabled {bool}    — from tenant-context; null = still loading
 *   fiscalPeriodId {number}  — active period; required for tool calls
 *   stageId        {number}  — active wizard stage; for wizard_readiness
 *
 * Governance:
 *   - Tier gate: handled server-side. Panel shows upgrade notice on 403.
 *   - NN-03: panel never triggers or suggests calculation actions.
 *   - All tool calls are GET only (Phase 1 tools).
 *   - Agent session is read/write (Phase 2 snapshot management only).
 *   - JWT forwarded via credentials: "include" (cookie-based auth).
 *   - Snapshot invalidation per agentic_spec_v1_1.md §10.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Sparkles, Bot, Activity, GitBranch,
  RefreshCw, AlertTriangle, CheckCircle2,
  ChevronRight, Loader2, AlertCircle, BookOpen,
} from "lucide-react";
import StateStepper from "./StateStepper";

const API_V1 = "/api/v1";
const API_V2 = "/api/v2";

// ---------------------------------------------------------------------------
// Shared fetch helper
// ---------------------------------------------------------------------------

function useFetch(url, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(url, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);

  return { data, loading, error };
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

function TabBtn({ active, onClick, icon: Icon, label, stale }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex flex-1 items-center justify-center gap-1.5 py-2 text-tok-xs font-medium transition border-b-2 ${
        active
          ? "border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]"
          : "border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {stale && (
        <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[rgb(var(--color-warning))]" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// State chip
// ---------------------------------------------------------------------------

function StateChip({ state }) {
  const tone = {
    active:          "text-[rgb(var(--color-primary))]",
    certified:       "text-[rgb(var(--color-success))]",
    open:            "text-[rgb(var(--color-primary))]",
    closed:          "text-[rgb(var(--color-warning))]",
    locked:          "text-[rgb(var(--color-text-muted))]",
    released:        "text-[rgb(var(--color-success))]",
    payroll_ready:   "text-[rgb(var(--color-success))]",
    credit_ready:    "text-[rgb(var(--color-primary))]",
    preview_ready:   "text-[rgb(var(--color-primary))]",
    cancelled:       "text-[rgb(var(--color-danger))]",
  }[state] ?? "text-[rgb(var(--color-text))]";

  return (
    <span className={`text-tok-xs font-semibold uppercase tracking-wide ${tone}`}>
      {state?.replace(/_/g, " ") ?? "—"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StaleBanner — shown when snapshot is stale, offers re-snapshot
// ---------------------------------------------------------------------------

function StaleBanner({ onRefresh, refreshing }) {
  return (
    <div className="flex items-center justify-between border-b border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface-muted))] px-3 py-2">
      <div className="flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-warning))]">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        Workflow state has changed
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline disabled:opacity-40"
      >
        {refreshing ? "Refreshing…" : "Re-snapshot"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WorkflowTab — S475 Phase 2
//
// Migrated from workflow-graph (state machine nodes) to workflow-context
// per unified_agentic_spec_v1_3 §5.2.
//
// Shows:
//   1. recommended_action — the single highest-priority action the comp admin
//      should take right now (§4.2: deterministic server logic, not AI)
//   2. setup_guide.chapters — 5-chapter period-close pipeline with state badges
//      (Data Import → Plans & Approval → Calculation → Statement Mgmt → Period Close)
//
// workflow-graph remains available to the Chat tab as an MCP tool (§9.1 Tool 5).
// This tab is a deterministic data read — no AI involved.
// ---------------------------------------------------------------------------

// Chapter state → StateStepper state mapping
// in_progress → current, complete → completed, blocked → blocked, not_started → pending
function chapterToStepperState(chapterState) {
  if (chapterState === "in_progress") return "current";
  if (chapterState === "complete")    return "completed";
  if (chapterState === "blocked")     return "blocked";
  return "pending";
}

// Action priority → badge colour
const _PRIORITY_CLS = {
  HIGH:   "bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))] border-[rgb(var(--color-danger))]/30",
  MEDIUM: "bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))] border-[rgb(var(--color-warning))]/30",
  INFO:   "bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] border-[rgb(var(--color-border))]",
};

function WorkflowTab({ fiscalPeriodId, snapshot, stale, onActionResolved }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // Single workflow-context call replaces workflow-graph (§5.2 migration)
  const url = fiscalPeriodId
    ? `${API_V2}/agent/workflow-context?fiscal_period_id=${fiscalPeriodId}&_k=${refreshKey}`
    : null;
  const { data, loading, error } = useFetch(url, [refreshKey]);

  const action   = data?.recommended_action ?? null;
  const guide    = data?.setup_guide ?? null;
  const chapters = guide?.chapters ?? [];
  const current  = guide?.current_chapter ?? null;

  // S654 OI-AGENT-RECOMMENDED-ACTION-WIRING — lift resolved action to root
  // so ChatTab can include it in both fetch bodies for journey doc injection.
  useEffect(() => {
    if (action && onActionResolved) onActionResolved(action);
  }, [action, onActionResolved]);

  const priorityCls = action ? (_PRIORITY_CLS[action.priority] ?? _PRIORITY_CLS.INFO) : "";

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          Next Action
        </span>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded p-1 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
          title="Refresh workflow context"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!fiscalPeriodId && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          No active fiscal period available.
        </p>
      )}

      {error && (
        <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-danger))]">
          {error === "403"
            ? "Workflow context requires Standard or Enterprise plan."
            : "Unable to load workflow state."}
        </div>
      )}

      {/* Kill-switch degraded state */}
      {data?.kill_switch_active === true && (
        <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8 px-3 py-2.5 text-tok-xs text-[rgb(var(--color-warning))]">
          Agent features are temporarily disabled.
        </div>
      )}

      {/* Recommended action card */}
      {action && !data?.kill_switch_active && (
        <div className={`rounded-xl border px-3 py-2.5 space-y-1 ${priorityCls}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-tok-xs font-semibold uppercase tracking-wide">
              {action.action_code?.replace(/_/g, " ")}
            </span>
            <span className={`rounded-full border px-1.5 py-0.5 text-tok-xs font-medium ${priorityCls}`}>
              {action.priority}
            </span>
          </div>
          {action.message && (
            <p className="text-tok-xs leading-relaxed opacity-90">{action.message}</p>
          )}
          {action.suggested_surface && (
            <p className="text-tok-xs opacity-60">
              {/* ACA-01: webapp_or_addin = left-nav or add-in are both valid */}
              {action.suggested_surface === "webapp_or_addin"
                ? "Available in: webapp or add-in"
                : action.suggested_surface === "webapp"
                ? "Available in: webapp"
                : "Available in: add-in"}
              {action.suggested_entity_type ? ` · ${action.suggested_entity_type}` : ""}
            </p>
          )}
        </div>
      )}

      {/* Loading placeholder for action */}
      {loading && !action && fiscalPeriodId && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[rgb(var(--color-text-muted))]" />
        </div>
      )}

      {/* Setup guide — 5-chapter pipeline via StateStepper (§4.2) */}
      {chapters.length > 0 && !data?.kill_switch_active && (
        <div className="space-y-2">
          <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
            Period Close Progress
          </span>
          <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-3">
            <StateStepper
              mode="vertical"
              steps={chapters.map((ch) => ({
                key: String(ch.number),
                label: `${ch.number}. ${ch.title}`,
              }))}
              currentStep={current !== null ? String(current) : undefined}
              completedSteps={chapters
                .filter((ch) => ch.state === "complete")
                .map((ch) => String(ch.number))}
              blockedSteps={chapters
                .filter((ch) => ch.state === "blocked")
                .map((ch) => String(ch.number))}
            />
          </div>
        </div>
      )}

      {/* Snapshot footer */}
      {snapshot && (
        <div className="text-tok-xs text-[rgb(var(--color-text-muted))] opacity-50 pt-1">
          Snapshot {snapshot.snapshot_id?.slice(0, 8)}… ·{" "}
          {snapshot.captured_at
            ? new Date(snapshot.captured_at).toLocaleTimeString()
            : "—"}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatusTab — S475 Phase 2
//
// Migrated from 4 separate API calls to a single workflow-context call
// per unified_agentic_spec_v1_3 §5.2.
//
// Shows:
//   1. Readiness gates  — preview_eligible, final_eligible, overall status,
//                         blocking issue count, domain breakdown
//   2. Latest calc run  — run_status, run_type, benchmark_check_passed
//   3. Fiscal period    — period label, status
//
// Single endpoint eliminates the N-request waterfall and ensures data
// consistency — all fields come from the same DB snapshot (§4.4).
// Deterministic data read — no AI involved.
// ---------------------------------------------------------------------------

function StatusTab({ fiscalPeriodId, stageId, snapshot }) {
  const [refreshKey, setRefreshKey] = useState(0);

  // Single workflow-context call replaces 4 fragmented endpoint calls (§5.2)
  const url = fiscalPeriodId
    ? `${API_V2}/agent/workflow-context?fiscal_period_id=${fiscalPeriodId}&_k=${refreshKey}`
    : null;
  const { data, loading, error } = useFetch(url, [refreshKey]);

  // Extract sections from unified response
  const readiness    = data?.readiness ?? null;
  const calcRun      = data?.calculation_run ?? null;
  const latestPrev   = calcRun?.latest_preview ?? null;
  const latestFinal  = calcRun?.latest_final ?? null;
  const latestRun    = latestFinal ?? latestPrev ?? null;
  const fp           = data?.fiscal_period ?? null;

  function GateRow({ label, value, ok }) {
    return (
      <div className="flex items-center justify-between py-1">
        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</span>
        <span className={`text-tok-xs font-semibold ${
          ok === true  ? "text-[rgb(var(--color-success))]" :
          ok === false ? "text-[rgb(var(--color-danger))]"  :
          "text-[rgb(var(--color-text))]"
        }`}>
          {value ?? "—"}
        </span>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          Live Status
        </span>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="rounded p-1 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
          title="Refresh status"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!fiscalPeriodId && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          No active fiscal period available.
        </p>
      )}

      {error && (
        <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-danger))]">
          {error === "403"
            ? "Status requires Standard or Enterprise plan."
            : "Unable to load status."}
        </div>
      )}

      {/* Fiscal period context */}
      {fp && (
        <div className="flex items-center justify-between">
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {fp.period_label ?? fp.period_code ?? `Period ${fiscalPeriodId}`}
          </span>
          <StateChip state={fp.period_status} />
        </div>
      )}

      {/* Readiness gates */}
      {readiness && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
          <div className="mb-2 text-tok-xs font-semibold text-[rgb(var(--color-text))]">Readiness Gates</div>
          <GateRow
            label="Preview eligible"
            value={readiness.preview_eligible === true ? "Yes" : "No"}
            ok={readiness.preview_eligible}
          />
          <GateRow
            label="Final eligible"
            value={readiness.final_eligible === true ? "Yes" : "No"}
            ok={readiness.final_eligible}
          />
          <GateRow
            label="Overall status"
            value={readiness.overall_status?.replace(/_/g, " ")}
            ok={null}
          />
          {(readiness.blocking_issue_count ?? 0) > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5 text-tok-xs text-[rgb(var(--color-danger))]">
              <AlertTriangle className="h-3 w-3 shrink-0" />
              {readiness.blocking_issue_count} blocking issue{readiness.blocking_issue_count !== 1 ? "s" : ""}
            </div>
          )}

          {/* Domain breakdown */}
          {readiness.domains?.length > 0 && (
            <div className="mt-2 space-y-0.5 border-t border-[rgb(var(--color-border))] pt-2">
              {readiness.domains.map((d) => (
                <div key={d.entity_type} className="flex items-center justify-between">
                  <span className="text-tok-xs text-[rgb(var(--color-text-muted))] capitalize">
                    {d.entity_type.replace(/_/g, " ")}
                  </span>
                  <span className="text-tok-xs text-[rgb(var(--color-text))]">
                    {d.min_status?.replace(/_/g, " ") ?? "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Latest calculation run */}
      {data && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2.5">
          <div className="mb-2 text-tok-xs font-semibold text-[rgb(var(--color-text))]">Latest Calculation Run</div>
          {latestRun ? (
            <>
              <GateRow label="Type"      value={latestRun.run_type}   ok={null} />
              <GateRow
                label="Status"
                value={latestRun.run_status}
                ok={latestRun.run_status === "completed"}
              />
              <GateRow
                label="Benchmark"
                value={
                  latestRun.benchmark_check_passed === true  ? "Passed" :
                  latestRun.benchmark_check_passed === false ? "Failed" : "—"
                }
                ok={latestRun.benchmark_check_passed}
              />
            </>
          ) : (
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">No runs for this period.</p>
          )}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && fiscalPeriodId && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] animate-pulse" />
          ))}
        </div>
      )}

      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] opacity-50">
        Phase 2 · Read-only · NN-03 enforced
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChatTab
// ---------------------------------------------------------------------------

function ChatTab({stageName, aiQueryEnabled, agentSessionId, fiscalPeriodId, fiscalPeriodLabel, fiscalPeriodStatus, stageId, wizardSessionStatus, snapshotId, recommendedAction,}) {
  const [input, setInput]             = useState("");
  const [messages, setMessages]       = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState(null);
  const [tierBlocked, setTierBlocked] = useState(false);
  const isBlocked = aiQueryEnabled === false || tierBlocked;
  const messagesEndRef    = useRef(null);
  // S589 OI-AGENT-ORIENTATION-TRIGGER — fires __ORIENTATION__ exactly once per
  // agent session. Survives tab switches. Does NOT re-fire on remount within
  // the same CopilotPanel session because the ref lives in this component and
  // the panel stays mounted while tabs switch (only content area swaps).
  const orientationSentRef = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ---------------------------------------------------------------------------
  // Orientation trigger — fires once when agentSessionId arrives and
  // fiscalPeriodId is set. Sends __ORIENTATION__ as a standard chat call.
  // No user bubble is added — only the assistant response is shown.
  // Guard: orientationSentRef prevents re-fire on tab switch or re-render.
  // Blocked if: aiQueryEnabled is false, tierBlocked, no fiscalPeriodId.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!agentSessionId || !fiscalPeriodId) return;
    if (aiQueryEnabled === false) return;
    if (orientationSentRef.current) return;
    orientationSentRef.current = true;

    // Add a placeholder assistant message that will be streamed into
    setMessages((prev) => [...prev, { role: "assistant", text: "" }]);
    setSubmitting(true);

    const history = []; // orientation fires at session start — no prior history

    fetch(`${API_V2}/agent/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        message:               "__ORIENTATION__",
        agent_session_id:      agentSessionId,
        fiscal_period_id:      fiscalPeriodId,
        fiscal_period_label:   fiscalPeriodLabel   ?? null,
        fiscal_period_status:  fiscalPeriodStatus  ?? null,
        stage_id:              stageId             ?? null,
        stage_name:            stageName           ?? null,
        wizard_session_status: wizardSessionStatus ?? null,
        snapshot_id:           snapshotId          ?? null,
        recommended_action:    recommendedAction   ?? null,
        conversation_history:  history,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (res.status === 403) {
            const code = body?.detail?.error?.code ?? body?.error ?? "";
            if (code === "FEATURE_UNAVAILABLE") {
              setTierBlocked(true);
              setMessages((prev) => prev.slice(0, -1));
              return;
            }
          }
          // Non-tier error — remove placeholder silently, orientation degrades gracefully
          setMessages((prev) => prev.slice(0, -1));
          return;
        }

        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed.startsWith("data: ")) continue;
            try {
              const event = JSON.parse(trimmed.slice(6));

              if (event.type === "chunk" && event.text) {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === "assistant") {
                    updated[updated.length - 1] = {
                      ...last,
                      text: last.text + event.text,
                    };
                  }
                  return updated;
                });
              }

              if (event.type === "error") {
                // Orientation error — remove placeholder, degrade silently
                setMessages((prev) => prev.slice(0, -1));
              }
            } catch {
              // Malformed SSE line — skip
            }
          }
        }
      })
      .catch(() => {
        // Network failure — remove placeholder, degrade silently
        setMessages((prev) => prev.slice(0, -1));
      })
      .finally(() => {
        setSubmitting(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSessionId, fiscalPeriodId]);

  async function handleSubmit() {
    const query = input.trim();
    if (!query || submitting) return;
    setInput("");
    setError(null);

    const history = messages
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, content: m.text }));

    setMessages((prev) => [...prev, { role: "user", text: query }]);
    setSubmitting(true);
    setMessages((prev) => [...prev, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${API_V2}/agent/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          message:               query,
          agent_session_id:      agentSessionId      ?? null,
          fiscal_period_id:      fiscalPeriodId      ?? null,
          fiscal_period_label:   fiscalPeriodLabel   ?? null,
          fiscal_period_status:  fiscalPeriodStatus  ?? null,
          stage_id:              stageId             ?? null,
          stage_name:            stageName           ?? null,
          wizard_session_status: wizardSessionStatus ?? null,
          snapshot_id:           snapshotId          ?? null,
          recommended_action:    recommendedAction   ?? null,
          conversation_history:  history,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 403) {
          const code = body?.detail?.error?.code ?? body?.error ?? "";
          if (code === "FEATURE_UNAVAILABLE") {
            setTierBlocked(true);
            setMessages((prev) => prev.slice(0, -2));
            setInput(query);
            return;
          }
        }
        throw new Error(
          body?.detail?.error?.message ?? body?.error?.message ?? `Error ${res.status}`
        );
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(trimmed.slice(6));

            if (event.type === "chunk" && event.text) {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = {
                    ...last,
                    text: last.text + event.text,
                  };
                }
                return updated;
              });
            }

            if (event.type === "error") {
              setError(event.message ?? "An error occurred.");
              setMessages((prev) => prev.slice(0, -1));
            }
          } catch {
            // Malformed SSE line — skip
          }
        }
      }
    } catch (e) {
      setError(e.message);
      setMessages((prev) => prev.slice(0, -2));
      setInput(query);
    } finally {
      setSubmitting(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isBlocked ? (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Copilot is available on Standard and Enterprise plans. Upgrade to access AI-assisted compensation analysis.
          </div>
        ) : messages.length === 0 ? (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] p-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Ask me anything about {stageName ?? "this stage"}, your data, or how to proceed.
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`rounded-xl px-3 py-2 text-tok-sm ${
              msg.role === "user"
                ? "ml-4 bg-[rgb(var(--color-primary))] text-white"
                : "mr-4 bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text))]"
            }`}>
              {msg.text}
            </div>
          ))
        )}
        {submitting && (
          <div className="mr-4 rounded-xl bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text-muted))] animate-pulse">
            Thinking…
          </div>
        )}
        {error && (
          <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-xs text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {!isBlocked && (
        <div className="border-t border-[rgb(var(--color-border))] p-3">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Copilot…"
              disabled={submitting}
              className="flex-1 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!input.trim() || submitting}
              className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-white disabled:opacity-40 hover:opacity-90 transition"
            >
              <Sparkles className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// GuideTab — S623
//
// Replaces the shell-level SIShellPanel (removed) and the per-panel inline
// System Intelligence cards (removed). Consolidates all contextual guidance
// into a single tab inside Copilot so the right rail is uncontested.
//
// Content is passed as a prop (guideContent) from CompAdminShell via the
// SI_CONTENT route map — CopilotPanel stays generic, shell owns the text.
// Zero API calls — purely static, route-keyed content.
// ---------------------------------------------------------------------------

function GuideTab({ guideContent, stageName }) {
  if (!guideContent) {
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
          <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
            {stageName ?? "Guide"}
          </span>
        </div>
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
          No guidance available for this screen. Ask Copilot in the Chat tab for help.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-3.5 w-3.5 text-[rgb(var(--color-primary))]" />
        <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
          {stageName ?? "Guide"}
        </span>
      </div>
      <div className="space-y-3 text-tok-sm text-[rgb(var(--color-text-muted))] leading-relaxed">
        {guideContent}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// CopilotPanel root — Phase 2: agent session lifecycle management
// OI-FISCAL-PERIOD-LABEL (S244): fiscal period label lifted to root so
// ChatTab system prompt receives a human-readable period name (e.g. "M01 2025")
// rather than falling back to the raw fiscal_period_id integer.
// ---------------------------------------------------------------------------

export default function CopilotPanel({
  open,
  stageName,
  aiQueryEnabled,
  fiscalPeriodId,
  stageId,
  guideContent,   // S623: JSX from SI_CONTENT[route] — replaces SIShellPanel
  killSwitchActive = false,  // S1035: disable panel when kill switch is active
}) {
  const [tab, setTab] = useState("chat");

  // OI-FISCAL-PERIOD-LABEL (S244) — derive human-readable label from period list.
  // Fetched once at root so ChatTab doesn't need its own fiscal period fetch.
  const [fiscalPeriodLabel, setFiscalPeriodLabel] = useState(null);

  useEffect(() => {
    if (!fiscalPeriodId) { setFiscalPeriodLabel(null); return; }
    fetch(`${API_V1}/fiscal-periods`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const periods = data?.data ?? [];
        const match = periods.find((p) => p.fiscal_period_id === fiscalPeriodId);
        setFiscalPeriodLabel(match?.period_label ?? match?.period_code ?? null);
      })
      .catch(() => setFiscalPeriodLabel(null));
  }, [fiscalPeriodId]);

  // S654 OI-AGENT-RECOMMENDED-ACTION-WIRING — lifted from WorkflowTab so
  // ChatTab can include it in both fetch bodies for journey doc injection.
  const [recommendedAction, setRecommendedAction] = useState(null);

  // S655 — resizable panel (drag handle on left edge, matches left nav behaviour)
  const PANEL_MIN     = 256;
  const PANEL_MAX     = 560;
  const PANEL_DEFAULT = 320; // w-80 equivalent
  const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT);
  const isResizing  = useRef(false);
  const resizeStart = useRef({ x: 0, width: PANEL_DEFAULT });

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isResizing.current  = true;
    resizeStart.current = { x: e.clientX, width: panelWidth };
  }, [panelWidth]);

  useEffect(() => {
    function onMouseMove(e) {
      if (!isResizing.current) return;
      // Panel is on the right edge — dragging left increases width
      const delta = resizeStart.current.x - e.clientX;
      const next  = Math.min(PANEL_MAX, Math.max(PANEL_MIN, resizeStart.current.width + delta));
      setPanelWidth(next);
    }
    function onMouseUp() {
      isResizing.current = false;
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };
  }, []);

  // Phase 2 — agent session state
  const [agentSessionId, setAgentSessionId] = useState(null);
  const [snapshot, setSnapshot]             = useState(null);
  const [stale, setStale]                   = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [refreshing, setRefreshing]         = useState(false);

  // Create agent session when panel opens and fiscalPeriodId is available
  useEffect(() => {
    if (!open || !fiscalPeriodId) return;
    // Don't re-create if session already exists for this period
    if (agentSessionId && snapshot?.fiscal_period_id === fiscalPeriodId) return;

    let cancelled = false;
    setSessionLoading(true);

    fetch(`${API_V2}/agent/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ fiscal_period_id: fiscalPeriodId }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setAgentSessionId(data.agent_session_id);
        setSnapshot(data.context_snapshot);
        setStale(false);
      })
      .catch(() => {
        // Session creation failed — panel degrades gracefully, tools still work
        if (!cancelled) {
          setAgentSessionId(null);
          setSnapshot(null);
        }
      })
      .finally(() => { if (!cancelled) setSessionLoading(false); });

    return () => { cancelled = true; };
  // Reset session when fiscal period changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fiscalPeriodId]);

  // Poll for stale detection every 60s while panel is open and session exists
  useEffect(() => {
    if (!open || !agentSessionId) return;

    const interval = setInterval(() => {
      fetch(`${API_V2}/agent/sessions/${agentSessionId}`, {
        credentials: "include",
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data?.stale === true) setStale(true);
        })
        .catch(() => {});
    }, 60_000);

    return () => clearInterval(interval);
  }, [open, agentSessionId]);

  // Re-snapshot on user request
  const handleRefreshSnapshot = useCallback(async () => {
    if (!agentSessionId || refreshing) return;
    setRefreshing(true);
    try {
      const res = await fetch(
        `${API_V2}/agent/sessions/${agentSessionId}/snapshot`,
        { method: "PATCH", credentials: "include" }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setSnapshot(data.context_snapshot);
      setStale(false);
    } catch {
      // Fail silently — stale banner remains, user can retry
    } finally {
      setRefreshing(false);
    }
  }, [agentSessionId, refreshing]);

  if (!open) return null;

  // S1035 — kill switch active: show halted state, block all agent interaction
  if (killSwitchActive) {
    return (
      <div
        className="relative flex h-full flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
        style={{ width: panelWidth, minWidth: PANEL_MIN, maxWidth: PANEL_MAX }}
      >
        <div
          onMouseDown={handleResizeStart}
          className="absolute left-0 top-0 h-full w-1 cursor-col-resize z-10 hover:bg-[rgb(var(--color-primary))]/30 transition-colors"
          title="Drag to resize"
        />
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text-muted))]">Copilot</span>
          </div>
          {stageName && (
            <span className="max-w-[120px] truncate text-tok-xs text-[rgb(var(--color-text-muted))]">
              {stageName}
            </span>
          )}
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--color-danger))]/10">
            <AlertTriangle className="h-6 w-6 text-[rgb(var(--color-danger))]" />
          </div>
          <div className="space-y-1">
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Agent halted
            </p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              The kill switch is active. All agent sessions are suspended.
              Contact your administrator or visit the Agent Trust Center to resume.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex h-full flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]"
      style={{ width: panelWidth, minWidth: PANEL_MIN, maxWidth: PANEL_MAX }}
    >
      {/* Resize handle — left edge, matches left nav drag behaviour */}
      <div
        onMouseDown={handleResizeStart}
        className="absolute left-0 top-0 h-full w-1 cursor-col-resize z-10 hover:bg-[rgb(var(--color-primary))]/30 transition-colors"
        title="Drag to resize"
      />
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-4 py-3">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-[rgb(var(--color-primary))]" />
          <span className="text-tok-sm font-semibold">Copilot</span>
          {sessionLoading && (
            <Loader2 className="h-3 w-3 animate-spin text-[rgb(var(--color-text-muted))]" />
          )}
        </div>
        {stageName && (
          <span className="max-w-[120px] truncate text-tok-xs text-[rgb(var(--color-text-muted))]">
            {stageName}
          </span>
        )}
      </div>

      {/* Stale banner — shown across all tabs when snapshot is stale */}
      {stale && (
        <StaleBanner onRefresh={handleRefreshSnapshot} refreshing={refreshing} />
      )}

      {/* Tabs */}
      <div className="flex border-b border-[rgb(var(--color-border))]">
        <TabBtn active={tab === "chat"}     onClick={() => setTab("chat")}     icon={Sparkles}  label="Chat" />
        <TabBtn active={tab === "workflow"} onClick={() => setTab("workflow")} icon={GitBranch} label="Workflow" stale={stale} />
        <TabBtn active={tab === "status"}   onClick={() => setTab("status")}   icon={Activity}  label="Status"   stale={stale} />
        <TabBtn active={tab === "guide"}    onClick={() => setTab("guide")}    icon={BookOpen}  label="Guide" />
      </div>

      {/* Tab content */}
      {tab === "chat" && (
        <ChatTab
          stageName={stageName}
          aiQueryEnabled={aiQueryEnabled}
          agentSessionId={agentSessionId}
          fiscalPeriodId={fiscalPeriodId}
          fiscalPeriodLabel={fiscalPeriodLabel}
          fiscalPeriodStatus={snapshot?.fiscal_period_status ?? null}
          stageId={stageId}
          wizardSessionStatus={snapshot?.wizard_session_status ?? null}
          snapshotId={snapshot?.snapshot_id ?? null}
          recommendedAction={recommendedAction}
        />
      )}
      {tab === "workflow" && (
        <WorkflowTab
          fiscalPeriodId={fiscalPeriodId}
          snapshot={snapshot}
          stale={stale}
          onActionResolved={setRecommendedAction}
        />
      )}
      {tab === "status" && (
        <StatusTab
          fiscalPeriodId={fiscalPeriodId}
          stageId={stageId}
          snapshot={snapshot}
        />
      )}
      {tab === "guide" && (
        <GuideTab
          guideContent={guideContent}
          stageName={stageName}
        />
      )}
    </div>
  );
}
